import React, { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";

//App
import { queryKeys } from "./constants";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../../utility/constants/constants";
import { SearchValue } from "../../../store/types";
import {
    CampaignDesignationsReportContextData,
    CampaignDesignationsReportUseState,
    FormikTypeaheadOption
} from "../../../types";
import reportApi from "../../../app/model/api/reportApi";
import agencyApi from "../../../app/model/api/agencyApi";
import CampaignDesignationsReport from "./CampaignDesignationsReport";

export const CampaignDesignationsReportContext =
    React.createContext<CampaignDesignationsReportContextData | null>(null);

const CampaignDesignationReportContainer = () => {
    const [state, setState] = useState<CampaignDesignationsReportUseState>({
    campaignId: null,
    organizationId: null,
    employeeId: null,
    agencyId: null,
    searchPayload: null,
    pageSize: GRID_PAGE_SIZE,
    currentPage: 1,
  });

  const campaignLookupData = useQuery({
    queryKey: [queryKeys.getOrganizationReportCampaignLookups],
    queryFn: () => reportApi.getCampaigns(),
    select: (data) => {
      const transformedData = data?.result?.map((dto) => ({
        label: dto.display as string,
        value: dto.id as string,
        organizationId: dto.parentId as string,
      }));
      return transformedData as FormikTypeaheadOption[];
    },
  });

  const reportLookups = useQuery({
    queryKey: [queryKeys.getOrganizationReportCampaignLookups, state.organizationId],
    queryFn: () => reportApi.getReportLookups(state.organizationId as string),
    enabled: state.organizationId != null,
    select: (data) => ({
      contributorTypes: data?.result?.contributorTypes?.map((dto) => ({
        label: dto.display as string,
        value: dto.id as string,
      })) as FormikTypeaheadOption[],
      methodOfGivingTypes: data?.result?.methodOfGivingTypes?.map((dto) => ({
        label: dto.display as string,
        value: dto.id as string,
      })) as FormikTypeaheadOption[],
    }),
  });

  const agencyLookupData = useQuery({
    queryKey: ["getCampaignDesignationReportAgencies"],
    queryFn: () =>
      agencyApi.searchAgency({
        isActive: true,
        pageNumber: 1,
        pageSize: 1000,
        sortBy: [{ field: "name", direction: 1 }],
      }),
    select: (data) =>
      data?.result?.map((dto) => ({
        label: dto.name as string,
        value: dto.id as string,
      })) as FormikTypeaheadOption[],
  });

  const campaignDesignationsReportDataQuery = useQuery({
    queryKey: [
      queryKeys.getCampaignDesignationsReportData,
      state.campaignId,
      state.searchPayload,
      state.pageSize,
      state.currentPage,
    ],
    queryFn: () =>
      reportApi.getCampaignDesignationsReportData({
        ...state.searchPayload,
        campaignId: state.campaignId ?? EMPTY_GUID,
        employeeId: state.employeeId ?? null,
        agencyId: state.searchPayload?.agencyId ?? state.agencyId,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
      }),
    placeholderData: keepPreviousData,
  });

  const updateCampaignId = async (campaignId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      campaignId: campaignId,
    }));
  };

  const updateOrganizationId = async (organizationId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      organizationId,
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

    const contextValue: CampaignDesignationsReportContextData = {
    id: state.campaignId,
    setId: updateCampaignId,
    campaigns: campaignLookupData?.data,
    contributorTypes: reportLookups.data?.contributorTypes,
    agencies: agencyLookupData.data,
    methodOfGivingTypes: reportLookups.data?.methodOfGivingTypes,
    setOrganizationId: updateOrganizationId,
    searchValue: state.searchPayload ?? undefined,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setCurrentPageSize: updateCurrentPageSize,
    paging: {
      currentPage: state.currentPage,
      totalSize: campaignDesignationsReportDataQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    refetchGrid: () => campaignDesignationsReportDataQuery?.refetch(),
    data: undefined,
  };

  if (campaignLookupData?.isLoading) return <LoadingIndicator />;

  return (
      <CampaignDesignationsReportContext.Provider value={contextValue}>
      <CampaignDesignationsReport
        data={campaignDesignationsReportDataQuery?.data?.result}
      />
    </CampaignDesignationsReportContext.Provider>
  );
};

export default CampaignDesignationReportContainer;
